import React from 'react'
import { Box, Divider, Paper, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  XAxis,
  YAxis,
  Tooltip,
  LineChart,
  Line,
  Legend,
  DotProps,
  ResponsiveContainer,
} from 'recharts'
import { ColoredCheckbox } from './ColoredCheckbox'

interface CustomDotProps extends DotProps {
  dataKey: string
  payload: {
    date: string
    count: number
  }
}

const CustomDot = (props: CustomDotProps): JSX.Element => {
  const { cy, cx, fill, r, stroke, strokeWidth, dataKey, payload } = props
  return (
    <circle
      cy={cy}
      cx={cx}
      fill={fill}
      r={payload.count ? r : 0}
      stroke={stroke}
      strokeWidth={strokeWidth}
      key={dataKey + '-' + payload.date}
      data-cy={dataKey + '-' + payload.date}
    />
  )
}

interface AlertAveragesGraphProps {
  data: React.ComponentProps<typeof LineChart>['data']
  loading: boolean
}

export default function AlertAveragesGraph(
  props: AlertAveragesGraphProps,
): JSX.Element {
  const theme = useTheme()
  const isLight = theme.palette.mode === 'light'

  const [visibility, setVisibility] = React.useState({
    mtta: true,
    mttr: true,
    resolved: true,
    avgEscalations: true,
  })

  const toggle = (key: keyof typeof visibility) => {
    setVisibility((v) => ({ ...v, [key]: !v[key] }))
  }

  return (
    <Box
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: isLight
          ? theme.palette.grey[50]
          : theme.palette.background.paper,
        boxShadow: isLight ? theme.shadows[1] : 'none',
        fontFamily: theme.typography.body2.fontFamily,
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          backgroundColor: isLight
            ? theme.palette.grey[100]
            : theme.palette.background.default,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 3,
            flexWrap: 'wrap',
          }}
        >
          <ColoredCheckbox
            label='MTTA'
            color={theme.palette.primary.main}
            checked={visibility.mtta}
            onToggle={() => toggle('mtta')}
          />
          <ColoredCheckbox
            label='MTTR'
            color={
              theme.palette.mode === 'light'
                ? theme.palette.info.dark
                : theme.palette.info.light
            }
            checked={visibility.mttr}
            onToggle={() => toggle('mttr')}
          />
          <ColoredCheckbox
            label='Resolved Time'
            color={
              theme.palette.mode === 'light'
                ? theme.palette.secondary.dark
                : theme.palette.secondary.light
            }
            checked={visibility.resolved}
            onToggle={() => toggle('resolved')}
          />
          <ColoredCheckbox
            label='Avg Escalations'
            color={theme.palette.warning.main}
            checked={visibility.avgEscalations}
            onToggle={() => toggle('avgEscalations')}
          />
        </Box>
      </Box>

      <Divider />
      <Box
        sx={{
          px: 2,
          py: 2,
          backgroundColor: isLight
            ? theme.palette.common.white
            : theme.palette.background.paper,
        }}
      >
        <Box sx={{ height: 400 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={props.data}>
              <XAxis
                dataKey='date'
                type='category'
                stroke={theme.palette.text.secondary}
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
              />
              <YAxis
                allowDecimals={false}
                stroke={theme.palette.text.secondary}
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
              />
              <Tooltip
                data-cy='metrics-tooltip'
                cursor={{ fill: theme.palette.background.default }}
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null

                  return (
                    <Paper variant='outlined' sx={{ p: 1 }}>
                       <Typography variant='body2'>{label}</Typography>
                       {payload.map((p: any) => (
                         <Typography key={p.name} variant='body2'>
                           {p.name}: {Math.round(p.value)} {p.name === 'Avg Escalations' ? '' : 'min'}
                         </Typography>
                       ))}
                    </Paper>
                  )
                }}
              />
              <Legend wrapperStyle={{ paddingTop: '20px' }} />
              <Line
                type='monotone'
                dataKey='avgTimeToAck'
                strokeOpacity={props.loading ? 0.5 : 1}
                strokeWidth={2}
                stroke={theme.palette.primary.main}
                activeDot={{ r: 8 }}
                isAnimationActive={false}
                dot={CustomDot}
                name='MTTA'
                hide={!visibility.mtta}
              />
              <Line
                type='monotone'
                strokeWidth={2}
                dataKey='avgTimeToClose'
                isAnimationActive={false}
                strokeOpacity={props.loading ? 0.5 : 1}
                stroke={
                  theme.palette.mode === 'light'
                    ? theme.palette.info.dark
                    : theme.palette.info.light
                }
                activeDot={{ r: 8 }}
                dot={CustomDot}
                name='MTTR'
                hide={!visibility.mttr}
              />
              <Line
                type='monotone'
                strokeWidth={2}
                dataKey='avgTimeToAckToClose'
                isAnimationActive={false}
                strokeOpacity={props.loading ? 0.5 : 1}
                stroke={
                  theme.palette.mode === 'light'
                    ? theme.palette.secondary.dark
                    : theme.palette.secondary.light
                }
                activeDot={{ r: 8 }}
                dot={CustomDot}
                name='Resolved Time'
                hide={!visibility.resolved}
              />
              <Line
                type='monotone'
                strokeWidth={2}
                dataKey='avgEscalations'
                isAnimationActive={false}
                strokeOpacity={props.loading ? 0.5 : 1}
                stroke={theme.palette.warning.main}
                activeDot={{ r: 8 }}
                dot={CustomDot}
                name='Avg Escalations'
                hide={!visibility.avgEscalations}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Box>
  )
}
